import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, TabBar } from './_ui';
import * as settingsApi from '../api/platform-settings.api.js';
import * as brandingApi from '../api/branding.api.js';
import * as emailProviderApi from '../api/email-provider.api.js';
import EmailProviderManager from '../shared/email/EmailProviderManager.jsx';

const DEFAULTS = {
  general: {
    platformName: 'URSA', supportEmail: '', adminEmail: '',
    defaultTimezone: 'Asia/Kolkata', defaultLanguage: 'en', dateFormat: 'DD/MM/YYYY',
    maintenanceMode: false, maintenanceMessage: '',
  },
  branding: {
    brandName: 'URSA', primaryColor: '#125AB0', secondaryColor: '#e8f0fe',
    logoUrl: '', faviconUrl: '', footerText: '© 2026 Nuacem AI',
    loginBackground: '',
  },
  smtp: {
    host: '', port: '587', username: '', password: '', encryption: 'tls',
    fromName: 'URSA', fromEmail: '', hasPassword: false,
  },
  security: {
    mfaRequired: false, sessionTimeoutMinutes: 60, maxLoginAttempts: 5,
    lockoutDurationMinutes: 15, passwordMinLength: 8,
    passwordRequireUppercase: true, passwordRequireNumbers: true,
    passwordRequireSymbols: false, passwordExpiryDays: 90,
    ipWhitelistEnabled: false, ipWhitelist: '',
  },
  storage: {
    defaultPlan: 'STARTER', maxUploadMb: 25, maxFileSizeMb: 100,
    allowedFileTypes: 'pdf,doc,docx,xls,xlsx,png,jpg,jpeg,csv', retentionDays: 365,
  },
  backup: {
    backupDir: '/backups',
    retentionDays: 30,
    compressionLevel: 6,
    schedule: 'manual',
    scheduleExpression: '0 2 * * *',
    cloudProvider: 'local',
    cloudBucket: '',
    cloudRegion: '',
    encryptionEnabled: false,
    maxBackupHistory: 50,
    autoCleanup: true,
  },
};

const TABS = [
  { id: 'general',  label: 'General',     icon: 'Settings'    },
  { id: 'branding', label: 'Branding',     icon: 'Palette'     },
  { id: 'smtp',     label: 'System Email', icon: 'Mail'        },
  { id: 'security', label: 'Security',     icon: 'ShieldCheck' },
  { id: 'storage',  label: 'Storage',      icon: 'HardDrive'   },
  { id: 'backup',   label: 'Backup',       icon: 'Database'    },
];

const VALID_TAB_IDS = new Set(TABS.map(t => t.id));

const Field = ({ label, children, hint }) => (
  <div className="nx-cc-field">
    <label className="nx-cc-field-label">{label}</label>
    {children}
    {hint && <span className="nx-muted nx-mini" style={{ marginTop: 4 }}>{hint}</span>}
  </div>
);

const Input = ({ value, onChange, type = 'text', placeholder, disabled }) => (
  <input
    className="nx-input"
    type={type}
    value={value ?? ''}
    onChange={e => onChange(e.target.value)}
    placeholder={placeholder}
    disabled={disabled}
  />
);

const Toggle = ({ value, onChange, label }) => (
  <label className="nx-cc-toggle">
    <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} />
    <span className="nx-cc-toggle-track"><span className="nx-cc-toggle-thumb" /></span>
    {label && <span className="nx-cc-toggle-label">{label}</span>}
  </label>
);

const Select = ({ value, onChange, options }) => (
  <select className="nx-input" value={value ?? ''} onChange={e => onChange(e.target.value)}>
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

const InfoBanner = ({ icon, children }) => (
  <div style={{
    display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 16px',
    background: 'var(--nx-info-bg, #eff6ff)', borderRadius: 8,
    border: '1px solid var(--nx-info-border, #bfdbfe)', marginBottom: 20,
  }}>
    <NexusIcon name={icon || 'Info'} size={16} style={{ color: 'var(--nx-info-icon, #3b82f6)', marginTop: 1, flexShrink: 0 }} />
    <span style={{ fontSize: 13, color: 'var(--nx-text)', lineHeight: 1.5 }}>{children}</span>
  </div>
);


const ComingSoon = ({ label }) => (
  <div className="nx-cc-coming-soon">
    <NexusIcon name="Construction" size={18} style={{ opacity: 0.4 }} />
    <span>{label} — Backend integration coming soon.</span>
  </div>
);

export default function PlatformSettings() {
  const toast                 = useToast();
  const [searchParams]        = useSearchParams();
  const initTab               = VALID_TAB_IDS.has(searchParams.get('tab')) ? searchParams.get('tab') : 'general';
  const [tab, setTab]         = useState(initTab);
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [saved, setSaved]             = useState(false);
  const [error, setError]             = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [loadError, setLoadError]         = useState(false);

  const update = useCallback((section, key, val) => {
    setSettings(s => ({ ...s, [section]: { ...s[section], [key]: val } }));
    setSaved(false);
  }, []);

  const handleLogoUpload = useCallback(async (file) => {
    if (!file?.type.startsWith('image/')) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Images only', desc: 'PNG, JPG, SVG, or WebP accepted.' });
      return;
    }
    setUploadingLogo(true);
    try {
      const { url } = await brandingApi.uploadPlatformBrandingAsset(file);
      update('branding', 'logoUrl', url);
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Logo upload failed', desc: e?.message });
    } finally {
      setUploadingLogo(false);
    }
  }, [update]);

  useEffect(() => {
    settingsApi.getAll()
      .then(data => setSettings(prev => {
        const merged = { ...prev };
        for (const sec of Object.keys(DEFAULTS)) {
          merged[sec] = { ...DEFAULTS[sec], ...data[sec] };
        }
        return merged;
      }))
      .catch(() => { setLoadError(true); })
      .finally(() => setLoading(false));
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = await settingsApi.updateSection(tab, settings[tab]);
      /* Sync form state with the server-confirmed values so the form always reflects
         what is actually stored in the DB, not just the local optimistic state. */
      if (saved && typeof saved === 'object') {
        setSettings(prev => {
          const merged = { ...prev };
          for (const sec of Object.keys(DEFAULTS)) {
            if (saved[sec]) merged[sec] = { ...DEFAULTS[sec], ...saved[sec] };
          }
          return merged;
        });
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      toast({ tone: 'success', icon: 'Check', title: 'Settings saved' });
      if (tab === 'branding') {
        /* Dispatch confirmed branding from server response so PlatformLayout applies
           exactly what is stored in DB (not local state that may differ post-sanitization). */
        const confirmedBranding = saved?.branding
          ? { ...DEFAULTS.branding, ...saved.branding }
          : settings.branding;
        globalThis.dispatchEvent(new CustomEvent('nx-platform-branding-saved', { detail: confirmedBranding }));
      }
    } catch (e) {
      setError(e?.message || 'Failed to save settings');
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Save failed', desc: e?.message });
    } finally {
      setSaving(false);
    }
  }, [tab, settings, toast]);

  const reset = useCallback(() => {
    setSettings(s => ({ ...s, [tab]: { ...DEFAULTS[tab] } }));
    setSaved(false);
    if (tab === 'branding') {
      globalThis.dispatchEvent(new CustomEvent('nx-platform-branding-saved', { detail: DEFAULTS.branding }));
    }
  }, [tab]);

  const g   = settings.general  || DEFAULTS.general;
  const b   = settings.branding || DEFAULTS.branding;
  const sec = settings.security || DEFAULTS.security;
  const st  = settings.storage  || DEFAULTS.storage;
  const bk  = settings.backup   || DEFAULTS.backup;

  if (loading) {
    return (
      <div className="nx-cc-page">
        <PageHead title="Platform Settings" sub="Configure platform-wide behavior and defaults" />
        <div className="nx-cc-skeleton" style={{ height: 400, marginTop: 16 }} />
      </div>
    );
  }

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Platform Settings"
        sub="Configure platform-wide behavior and defaults"
        actions={tab !== 'smtp' && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {saved && <Pill tone="green">Saved</Pill>}
            {error && <span style={{ color: 'var(--nx-danger)', fontSize: 13 }}>{error}</span>}
            <Btn variant="secondary" icon="RotateCcw" onClick={reset} disabled={saving}>Reset Defaults</Btn>
            <Btn variant="primary" icon="Save" onClick={save} disabled={saving || loadError}>
              {saving ? 'Saving…' : 'Save Changes'}
            </Btn>
          </div>
        )}
      />

      <TabBar tabs={TABS} active={tab} onChange={t => { setTab(t); setSaved(false); setError(null); }} />

      <div className="nx-cc-settings-body">

        {loadError && (
          <InfoBanner icon="AlertTriangle">
            Settings could not be loaded from the server — displaying defaults. Refresh the page or re-login to restore your saved configuration. Saving is disabled to prevent overwriting persisted data.
          </InfoBanner>
        )}

        {tab === 'general' && (
          <div className="nx-cc-settings-section">
            <div className="nx-cc-settings-grid">
              <Field label="Platform Name">
                <Input value={g.platformName} onChange={v => update('general', 'platformName', v)} placeholder="URSA" />
              </Field>
              <Field label="Support Email">
                <Input type="email" value={g.supportEmail} onChange={v => update('general', 'supportEmail', v)} placeholder="support@yourdomain.com" />
              </Field>
              <Field label="Admin Email">
                <Input type="email" value={g.adminEmail} onChange={v => update('general', 'adminEmail', v)} placeholder="admin@yourdomain.com" />
              </Field>
              <Field label="Default Timezone">
                <Select value={g.defaultTimezone} onChange={v => update('general', 'defaultTimezone', v)} options={[
                  { value: 'Asia/Kolkata',     label: 'Asia/Kolkata (IST)'    },
                  { value: 'UTC',              label: 'UTC'                    },
                  { value: 'America/New_York', label: 'America/New_York (EST)' },
                  { value: 'Europe/London',    label: 'Europe/London (GMT)'    },
                  { value: 'Asia/Dubai',       label: 'Asia/Dubai (GST)'       },
                  { value: 'Asia/Singapore',   label: 'Asia/Singapore (SGT)'   },
                ]} />
              </Field>
              <Field label="Default Language">
                <Select value={g.defaultLanguage} onChange={v => update('general', 'defaultLanguage', v)} options={[
                  { value: 'en', label: 'English' },
                  { value: 'hi', label: 'Hindi'   },
                  { value: 'ta', label: 'Tamil'   },
                ]} />
              </Field>
              <Field label="Date Format">
                <Select value={g.dateFormat} onChange={v => update('general', 'dateFormat', v)} options={[
                  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
                  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
                  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
                ]} />
              </Field>
            </div>

            <div className="nx-cc-chart-card" style={{ marginTop: 20 }}>
              <div className="nx-cc-chart-head">
                <span className="nx-cc-chart-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <NexusIcon name="AlertTriangle" size={14} style={{ color: 'var(--nx-danger)' }} />
                  Maintenance Mode
                </span>
                <Toggle
                  value={g.maintenanceMode}
                  onChange={v => update('general', 'maintenanceMode', v)}
                  label={g.maintenanceMode ? 'ON — platform is read-only' : 'OFF'}
                />
              </div>
              {g.maintenanceMode && (
                <div style={{ marginTop: 12 }}>
                  <Field label="Maintenance Message">
                    <textarea
                      className="nx-input"
                      rows={3}
                      value={g.maintenanceMessage}
                      onChange={e => update('general', 'maintenanceMessage', e.target.value)}
                    />
                  </Field>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'branding' && (
          <div className="nx-cc-settings-section">
            <div className="nx-cc-settings-grid">
              <Field label="Brand Name">
                <Input value={b.brandName} onChange={v => update('branding', 'brandName', v)} placeholder="URSA" />
              </Field>
              <Field label="Primary Color" hint="Used for navigation, buttons, and key UI elements.">
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="color" value={b.primaryColor || '#125AB0'} onChange={e => update('branding', 'primaryColor', e.target.value)}
                    style={{ width: 36, height: 34, borderRadius: 6, cursor: 'pointer', border: '1px solid var(--nx-border)', padding: 2 }} />
                  <Input value={b.primaryColor} onChange={v => update('branding', 'primaryColor', v)} placeholder="#125AB0" />
                </div>
              </Field>
              <Field label="Secondary Color" hint="Used for hover states, tints, and accent backgrounds.">
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="color" value={b.secondaryColor || '#e8f0fe'} onChange={e => update('branding', 'secondaryColor', e.target.value)}
                    style={{ width: 36, height: 34, borderRadius: 6, cursor: 'pointer', border: '1px solid var(--nx-border)', padding: 2 }} />
                  <Input value={b.secondaryColor} onChange={v => update('branding', 'secondaryColor', v)} placeholder="#e8f0fe" />
                </div>
              </Field>
              <Field label="Footer Text">
                <Input value={b.footerText} onChange={v => update('branding', 'footerText', v)} placeholder="© 2026 Nuacem AI" />
              </Field>
              <Field label="Login Background URL" hint="Full-page background image shown on the login screen.">
                <Input value={b.loginBackground} onChange={v => update('branding', 'loginBackground', v)} placeholder="https://..." />
              </Field>
              <Field label="Logo" hint="Upload an image file or paste a URL below.">
                <input type="file" accept="image/*" className="nx-input" style={{ padding: '6px 8px' }} disabled={uploadingLogo}
                  onChange={e => { const f = e.target.files?.[0]; if (f) { handleLogoUpload(f); } e.target.value = ''; }} />
                {uploadingLogo && <span style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 4 }}>Uploading…</span>}
                {b.logoUrl && !uploadingLogo && (
                  <img src={b.logoUrl} alt="Logo preview" style={{ height: 40, marginTop: 8, borderRadius: 4, objectFit: 'contain', background: '#f5f5f5' }} />
                )}
                <Input value={b.logoUrl} onChange={v => update('branding', 'logoUrl', v)} placeholder="or paste a URL: https://..." />
              </Field>
              <Field label="Favicon URL">
                <Input value={b.faviconUrl} onChange={v => update('branding', 'faviconUrl', v)} placeholder="https://..." />
              </Field>
            </div>
            <ComingSoon label="Custom CSS theming, white-label domain settings" />
          </div>
        )}

        {tab === 'smtp' && (
          <div className="nx-cc-settings-section">
            <EmailProviderManager api={emailProviderApi.platform} />
          </div>
        )}

        {tab === 'security' && (
          <div className="nx-cc-settings-section">
            <div className="nx-cc-chart-card">
              <div className="nx-cc-chart-head">
                <span className="nx-cc-chart-title">Session &amp; Login</span>
              </div>
              <div className="nx-cc-settings-grid" style={{ marginTop: 12 }}>
                <Field label="Session Timeout (minutes)" hint="Users are logged out after this period of inactivity.">
                  <Input type="number" value={sec.sessionTimeoutMinutes} onChange={v => update('security', 'sessionTimeoutMinutes', +v)} />
                </Field>
                <Field label="Max Login Attempts">
                  <Input type="number" value={sec.maxLoginAttempts} onChange={v => update('security', 'maxLoginAttempts', +v)} />
                </Field>
                <Field label="Lockout Duration (minutes)">
                  <Input type="number" value={sec.lockoutDurationMinutes} onChange={v => update('security', 'lockoutDurationMinutes', +v)} />
                </Field>
              </div>
              <div style={{ marginTop: 12 }}>
                <Toggle value={sec.mfaRequired} onChange={v => update('security', 'mfaRequired', v)} label="Require MFA for all platform users" />
              </div>
            </div>

            <div className="nx-cc-chart-card" style={{ marginTop: 16 }}>
              <div className="nx-cc-chart-head">
                <span className="nx-cc-chart-title">Password Policy</span>
              </div>
              <div className="nx-cc-settings-grid" style={{ marginTop: 12 }}>
                <Field label="Minimum Length">
                  <Input type="number" value={sec.passwordMinLength} onChange={v => update('security', 'passwordMinLength', +v)} />
                </Field>
                <Field label="Password Expiry (days)" hint="0 = never expires.">
                  <Input type="number" value={sec.passwordExpiryDays} onChange={v => update('security', 'passwordExpiryDays', +v)} />
                </Field>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
                <Toggle value={sec.passwordRequireUppercase} onChange={v => update('security', 'passwordRequireUppercase', v)} label="Require uppercase letters" />
                <Toggle value={sec.passwordRequireNumbers}   onChange={v => update('security', 'passwordRequireNumbers',   v)} label="Require numbers"          />
                <Toggle value={sec.passwordRequireSymbols}   onChange={v => update('security', 'passwordRequireSymbols',   v)} label="Require symbols"          />
              </div>
            </div>

            <div className="nx-cc-chart-card" style={{ marginTop: 16 }}>
              <div className="nx-cc-chart-head">
                <span className="nx-cc-chart-title">IP Allowlist</span>
              </div>
              <div style={{ marginTop: 12 }}>
                <Toggle value={sec.ipWhitelistEnabled} onChange={v => update('security', 'ipWhitelistEnabled', v)} label="Enable IP allowlist for platform access" />
              </div>
              {sec.ipWhitelistEnabled && (
                <Field label="Allowed IPs (comma-separated CIDR)" hint="e.g. 192.168.1.0/24, 10.0.0.1">
                  <textarea
                    className="nx-input"
                    rows={3}
                    value={sec.ipWhitelist}
                    onChange={e => update('security', 'ipWhitelist', e.target.value)}
                    placeholder="0.0.0.0/0"
                  />
                </Field>
              )}
            </div>
          </div>
        )}

        {tab === 'storage' && (
          <div className="nx-cc-settings-section">
            <div className="nx-cc-settings-grid">
              <Field label="Default Organization Plan" hint="Applied when a new organization is created without an explicit plan.">
                <Select value={st.defaultPlan} onChange={v => update('storage', 'defaultPlan', v)} options={[
                  { value: 'STARTER',      label: 'Starter'      },
                  { value: 'PROFESSIONAL', label: 'Professional' },
                  { value: 'BUSINESS',     label: 'Business'     },
                  { value: 'ENTERPRISE',   label: 'Enterprise'   },
                  { value: 'CUSTOM',       label: 'Custom'       },
                ]} />
              </Field>
              <Field label="Max Single Upload (MB)">
                <Input type="number" value={st.maxUploadMb} onChange={v => update('storage', 'maxUploadMb', +v)} />
              </Field>
              <Field label="Max File Size (MB)">
                <Input type="number" value={st.maxFileSizeMb} onChange={v => update('storage', 'maxFileSizeMb', +v)} />
              </Field>
              <Field label="File Retention (days)" hint="0 = retain indefinitely.">
                <Input type="number" value={st.retentionDays} onChange={v => update('storage', 'retentionDays', +v)} />
              </Field>
              <Field label="Allowed File Types" hint="Comma-separated extensions.">
                <Input value={st.allowedFileTypes} onChange={v => update('storage', 'allowedFileTypes', v)} placeholder="pdf,docx,png,jpg" />
              </Field>
            </div>
            <ComingSoon label="Cloud storage backends (S3, GCS, Azure Blob), CDN configuration" />
          </div>
        )}

        {tab === 'backup' && (
          <div className="nx-cc-settings-section">
            <div className="nx-cc-chart-card">
              <div className="nx-cc-chart-head">
                <span className="nx-cc-chart-title">Storage &amp; Retention</span>
              </div>
              <div className="nx-cc-settings-grid" style={{ marginTop: 12 }}>
                <Field label="Backup Directory" hint="Absolute path on the server where backup archives are written.">
                  <Input value={bk.backupDir} onChange={v => update('backup', 'backupDir', v)} placeholder="/backups" />
                </Field>
                <Field label="Retention Days" hint="Backups older than this are eligible for automatic cleanup.">
                  <Input type="number" value={bk.retentionDays} onChange={v => update('backup', 'retentionDays', +v)} />
                </Field>
                <Field label="Compression Level" hint="gzip level 1 (fastest) to 9 (smallest). Default 6.">
                  <Select value={String(bk.compressionLevel)} onChange={v => update('backup', 'compressionLevel', +v)} options={[
                    { value: '1', label: '1 — Fastest'  },
                    { value: '3', label: '3 — Fast'     },
                    { value: '6', label: '6 — Balanced (default)' },
                    { value: '9', label: '9 — Smallest' },
                  ]} />
                </Field>
                <Field label="Maximum Backup History" hint="Maximum number of backup records to keep in the history table.">
                  <Input type="number" value={bk.maxBackupHistory} onChange={v => update('backup', 'maxBackupHistory', +v)} />
                </Field>
              </div>
              <div style={{ marginTop: 14 }}>
                <Toggle value={bk.autoCleanup} onChange={v => update('backup', 'autoCleanup', v)} label="Automatic cleanup — delete expired backups after retention window" />
              </div>
            </div>

            <div className="nx-cc-chart-card" style={{ marginTop: 16 }}>
              <div className="nx-cc-chart-head">
                <span className="nx-cc-chart-title">Schedule</span>
              </div>
              <div className="nx-cc-settings-grid" style={{ marginTop: 12 }}>
                <Field label="Backup Schedule">
                  <Select value={bk.schedule} onChange={v => update('backup', 'schedule', v)} options={[
                    { value: 'manual',    label: 'Manual — on-demand only'   },
                    { value: 'cron',      label: 'Cron — automated schedule' },
                  ]} />
                </Field>
                {bk.schedule === 'cron' && (
                  <Field label="Cron Expression" hint="Standard 5-field cron. Example: 0 2 * * * = daily at 02:00.">
                    <Input value={bk.scheduleExpression} onChange={v => update('backup', 'scheduleExpression', v)} placeholder="0 2 * * *" />
                  </Field>
                )}
              </div>
            </div>

            <div className="nx-cc-chart-card" style={{ marginTop: 16 }}>
              <div className="nx-cc-chart-head">
                <span className="nx-cc-chart-title">Cloud Destination</span>
              </div>
              <div className="nx-cc-settings-grid" style={{ marginTop: 12 }}>
                <Field label="Cloud Provider">
                  <Select value={bk.cloudProvider} onChange={v => update('backup', 'cloudProvider', v)} options={[
                    { value: 'local', label: 'Local (server filesystem)' },
                    { value: 's3',    label: 'Amazon S3'                 },
                    { value: 'azure', label: 'Azure Blob Storage'        },
                    { value: 'gcs',   label: 'Google Cloud Storage'      },
                  ]} />
                </Field>
                {bk.cloudProvider !== 'local' && (
                  <>
                    <Field label="Bucket / Container">
                      <Input value={bk.cloudBucket} onChange={v => update('backup', 'cloudBucket', v)} placeholder="my-backup-bucket" />
                    </Field>
                    <Field label="Region">
                      <Input value={bk.cloudRegion} onChange={v => update('backup', 'cloudRegion', v)} placeholder="ap-south-1" />
                    </Field>
                  </>
                )}
              </div>
              <div style={{ marginTop: 14 }}>
                <Toggle value={bk.encryptionEnabled} onChange={v => update('backup', 'encryptionEnabled', v)} label="Encrypt backup archives before upload" />
              </div>
              {bk.cloudProvider === 'local' && (
                <div className="nx-cc-coming-soon" style={{ marginTop: 14 }}>
                  <NexusIcon name="Cloud" size={16} style={{ opacity: 0.4 }} />
                  <span>Cloud destinations (S3, GCS, Azure Blob) — select a cloud provider above to configure.</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
